import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Table } from 'src/design/molecules/Table';
import { Tabs } from 'src/design/molecules/Tabs';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { DatePicker } from 'src/design/atoms/DatePicker';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { useDailyProgressHistory } from 'src/api/queries/dailyProgress/useDailyProgressHistory';
import { useCreateDailyProgress } from 'src/api/queries/dailyProgress/useDailyProgressMutations';
import { CTCLinkIndicator } from 'src/routes/Jobs/WBS/components/CTCLinkIndicator';
import { ProgressManagedIndicator } from 'src/routes/Jobs/WBS/components/ProgressManagedIndicator';
import {
  DailyProgressItemsRequestDto,
  ProgressItemEntryDto,
} from 'src/api/FiretrolApiClient';
import { formatDate, convertAccountsToTreeData } from 'src/utils';
import { useJobs } from 'src/hooks/useJobs';
import { WbsTreeData } from 'src/routes/Jobs/WBS/components/types';
import { useWbs } from 'src/routes/Jobs/WBS/contexts/WbsContext';

interface ProgressRowData {
  id: string;
  name: string;
  depth: number;
  isLeaf: boolean;
  progressManaged: boolean;
  estimatedQuantity: number;
  actualQuantity: number;
  unitOfMeasure?: string;
  node: WbsTreeData;
  subRows?: ProgressRowData[];
}

interface PhysicalProgressInputProps {
  jobNumber: string;
  jobId: number;
  canEdit: boolean;
  initialProgress: ProgressItemEntryDto[];
  onProgressChange?: (progress: ProgressItemEntryDto[]) => void;
  onProgressSaved: () => void;
}

const formatValue = (value: number) => {
  return Number(value).toFixed(0);
};

const calculateAggregatedValues = (
  nodes: ProgressRowData[],
): {
  estimatedQuantity: number;
  actualQuantity: number;
} => {
  const leafNodes = nodes.filter((node) => node.isLeaf);

  if (leafNodes.length === 0) {
    return {
      estimatedQuantity: 0,
      actualQuantity: 0,
    };
  }

  const summed = leafNodes.reduce(
    (acc, node) => {
      acc.estimatedQuantity += node.estimatedQuantity;
      acc.actualQuantity += node.actualQuantity;
      return acc;
    },
    {
      estimatedQuantity: 0,
      actualQuantity: 0,
    },
  );

  return summed;
};

const transformWbsToTableData = (
  nodes: WbsTreeData[],
  depth = 0,
): ProgressRowData[] => {
  const rows: ProgressRowData[] = [];

  nodes.forEach((node) => {
    const shouldInclude =
      node.apply || (node.children && node.children.length > 0);
    if (!shouldInclude) return;

    const isLeaf = node.apply && (!node.children || node.children.length === 0);

    if (isLeaf) {
      const estimatedQuantity = Number(node.currentQuantityEstimate);
      const actualQuantity = Number(node.currentQuantityActual);

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: true,
        progressManaged: node.progressManaged === true,
        estimatedQuantity,
        actualQuantity,
        unitOfMeasure: node.unitOfMeasure,
        node,
      });
    } else {
      const childRows = node.children
        ? transformWbsToTableData(node.children, depth + 1)
        : [];

      const aggregated = calculateAggregatedValues(childRows);

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: false,
        progressManaged: false,
        estimatedQuantity: aggregated.estimatedQuantity,
        actualQuantity: aggregated.actualQuantity,
        unitOfMeasure: node.unitOfMeasure,
        node,
        subRows: childRows,
      });
    }
  });

  return rows;
};

export const PhysicalProgressInput: React.FC<PhysicalProgressInputProps> = ({
  jobId,
  jobNumber,
  initialProgress,
  onProgressChange,
  onProgressSaved,
  canEdit = false,
}) => {
  const { wbsData: wbsMaster, accountMap, loading: wbsLoading } = useWbs();
  const [wbs, setWbs] = React.useState<WbsTreeData[]>([]);
  const [values, setValues] =
    React.useState<ProgressItemEntryDto[]>(initialProgress);
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const { selectedJob } = useJobs();
  const createProgress = useCreateDailyProgress();

  React.useEffect(() => {
    if (!wbsMaster) return;

    const filteredTreeData = convertAccountsToTreeData(
      wbsMaster.wbsAccount,
      (account) =>
        account.active &&
        !account.archived &&
        account.accountType === 'labor' &&
        !account.timesheetsOnly,
    );
    setWbs(filteredTreeData);

    // Initialize progress values for new charge codes (only leaf nodes)
    const leafNodes: WbsTreeData[] = [];
    const traverseLeafNodes = (nodes: WbsTreeData[]) => {
      nodes.forEach((node) => {
        if (
          node.active &&
          node.apply &&
          node.accountType === 'labor' &&
          !node.progressManaged &&
          (!node.children || node.children.length === 0)
        ) {
          leafNodes.push(node);
        } else if (node.children) {
          traverseLeafNodes(node.children);
        }
      });
    };
    traverseLeafNodes(filteredTreeData);

    const vs = leafNodes.map((t: WbsTreeData) => {
      const exists = values.find((v) => v.wbsAccountId === t.id);
      if (exists) return exists;
      return {
        wbsAccountId: t.id,
        value: 0,
      };
    });
    setValues(vs);
  }, [wbsMaster]);

  React.useEffect(() => {
    if (onProgressChange) {
      onProgressChange(values);
    }
  }, [values]);

  const saveProgress = () => {
    if (!selectedJob) return;

    const request: DailyProgressItemsRequestDto = {
      jobId: selectedJob.id,
      jobNumber: jobNumber,
      date: selectedDate,
      progressItems: values.filter((v) => v.value > 0), // Only send items with values
    };

    createProgress.mutate(
      { jobId: selectedJob.id, request },
      {
        onSuccess: () => {
          setValues((prevState) =>
            prevState.map((prev) => ({ ...prev, value: 0 })),
          );
          onProgressSaved();
        },
      },
    );
  };

  const updateValue = (wbsAccountId: string, value: number) => {
    setValues((prevState) => {
      return prevState.map((i) => {
        if (i.wbsAccountId === wbsAccountId) {
          return {
            ...i,
            value,
          };
        }
        return i;
      });
    });
  };

  const onFocus = (e: any) => {
    e.target.select();
  };

  const tableData = useMemo(() => {
    return transformWbsToTableData(wbs);
  }, [wbs]);

  const columns = useMemo<MRT_ColumnDef<ProgressRowData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Charge Code',
        Cell: ({ row }) => (
          <Group gap="xs" align="center">
            <Text c="black">{row.original.name}</Text>
            <CTCLinkIndicator node={row.original.node} size="xs" />
            <ProgressManagedIndicator node={row.original.node} size="xs" />
          </Group>
        ),
      },
      {
        id: 'estimatedQuantity',
        size: 150,
        accessorFn: (row) => row.estimatedQuantity,
        header: 'Estimated Installed',
        Cell: ({ cell }) => (
          <Text c="black">{formatValue(cell.getValue<number>())}</Text>
        ),
      },
      {
        id: 'actualQuantity',
        size: 150,
        accessorFn: (row) => row.actualQuantity,
        header: 'Current Installed',
        Cell: ({ cell }) => (
          <Text c="black">{formatValue(cell.getValue<number>())}</Text>
        ),
      },
      {
        id: 'inputQuantity',
        size: 150,
        header: 'Installed Quantity',
        Cell: ({ row }) => {
          if (!row.original.isLeaf) {
            return <Text c="dimmed">-</Text>;
          }

          if (row.original.progressManaged) {
            return <Text c="dimmed">-</Text>;
          }

          const target = values.find((v) => v.wbsAccountId === row.original.id);
          if (!target) return <Text c="dimmed">-</Text>;

          if (!canEdit) {
            return <Text>{formatValue(target.value)}</Text>;
          }

          return (
            <NumberInput
              onFocus={onFocus}
              min={0}
              value={target.value}
              onChange={(value) => {
                if (!isNaN(Number(value))) {
                  updateValue(row.original.id, Number(value));
                }
              }}
            />
          );
        },
      },
    ],
    [values, canEdit],
  );

  const tableState = useTableState({
    identifier: 'progress-input-table',
    columns: columns,
  });

  const tableExports = useTableExports<ProgressRowData>(
    'physical-progress',
    (data) => {
      const flattenData = (items: ProgressRowData[], prefix = ''): any[] => {
        return items.flatMap((item) => {
          const current = {
            ['Job Name']: selectedJob?.name,
            ['Job Number']: selectedJob?.jobNumber,
            ['Name']: prefix + item.name,
            ['Estimated Quantity']: item.estimatedQuantity,
            ['Actual Quantity']: item.actualQuantity,
            ['Completion %']:
              (item.actualQuantity / item.estimatedQuantity) * 100 || 0,
          };
          if (item.subRows && item.subRows.length > 0) {
            return [current, ...flattenData(item.subRows, prefix + '  ')];
          }
          return [current];
        });
      };
      return flattenData(data);
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableExpanding: true,
      enableExpandAll: true,
      enableBottomToolbar: false,
      enableTopToolbar: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: false,
      columns: columns,
      data: tableData,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableRowActions: false,
      enableClickToCopy: false,
      initialState: {
        expanded: {},
      },
    },
    getRowId: (row) => row.id,
    totalCount: tableData.length,
    isLoading: wbsLoading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return (
    <Paper mb="md">
      <Title order={4}>Physical Progress</Title>
      <Stack gap="md">
        {wbsLoading ? (
          <Box style={{ textAlign: 'center', padding: '40px' }}>
            <Loader />
          </Box>
        ) : (
          <>
            {values.length === 0 ? (
              <Text ta="center" py="xl">
                No active material charge codes. Update charge codes in the job
                WBS settings.
              </Text>
            ) : (
              <Tabs defaultValue="progress" mt="md" keepMounted={false}>
                <Tabs.List justify="center">
                  <Tabs.Tab value="progress">Progress</Tabs.Tab>
                  <Tabs.Tab value="history">History</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="progress">
                  <Stack gap="md">
                    <Box>
                      <DatePicker
                        label="Date Completed"
                        value={selectedDate}
                        onChange={(value) => {
                          if (value) {
                            setSelectedDate(value);
                          }
                        }}
                      />
                    </Box>

                    {table}

                    {canEdit && (
                      <Box>
                        <BasicButton
                          disabled={
                            createProgress.isPending ||
                            values.length === 0 ||
                            values.every((v) => v.value === 0)
                          }
                          onClick={saveProgress}
                          loading={createProgress.isPending}
                        >
                          Save Progress for {formatDate(selectedDate)}
                        </BasicButton>
                      </Box>
                    )}
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="history">
                  {selectedJob && (
                    <HistoryTab
                      jobId={selectedJob.id}
                      accountMap={accountMap}
                    />
                  )}
                </Tabs.Panel>
              </Tabs>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};

interface HistoryTabProps {
  jobId: number;
  accountMap: Map<string, WbsTreeData>;
}

const HistoryTab: React.FC<HistoryTabProps> = ({ jobId, accountMap }) => {
  const { data: history, isLoading: loading } = useDailyProgressHistory(jobId);
  const data = history ?? [];

  const formatValue = (value: number) => {
    return value.toLocaleString();
  };

  const renderRows = () => {
    return data
      .sort(
        (a, b) =>
          moment(a.date).toDate().getTime() - moment(b.date).toDate().getTime(),
      )
      .map((d) => {
        // Look up the WBS account using the wbsAccountId
        const w = accountMap.get(d.wbsAccountId || '');
        if (!w) return null;

        const chargeCodeName = (
          <Group gap="xs" align="center">
            {w?.path || w?.name || `Unknown Charge Code (${d.wbsAccountId})`}
            <CTCLinkIndicator node={w} size="xs" />
          </Group>
        );

        return (
          <Table.Tr key={d.id}>
            <Table.Td>{chargeCodeName}</Table.Td>
            <Table.Td>{formatDate(d.date)}</Table.Td>
            <Table.Td>{formatValue(d.value || 0)}</Table.Td>
            <Table.Td>{d.createdBy?.fullName || 'N/A'}</Table.Td>
          </Table.Tr>
        );
      });
  };

  if (loading) {
    return (
      <Box style={{ textAlign: 'center', padding: '40px' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box mt="md">
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Charge Code</Table.Th>
            <Table.Th>Install Date</Table.Th>
            <Table.Th>Installed Quantity</Table.Th>
            <Table.Th>Entered By</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{renderRows()}</Table.Tbody>
      </Table>
    </Box>
  );
};
